import { offlineDb } from './offline-db';
import { pickApiField as pickDirectoryField } from './offline-field-utils';

/** Dexie key for saved offline-login identities (separate from `users` org directory). */
const OFFLINE_LOGIN_USERS_KEY = 'offline_login_users';

const LEGACY_PASSWORD_SALT = '_adams_offline_salt';
const KDF_ITERATIONS = 120000;
const HASH_VERSION = 'v2';
const SALT_BYTES = 16;
const DERIVED_BITS = 256;

function getCrypto() {
  if (typeof crypto !== 'undefined' && crypto?.subtle) return crypto;
  throw new Error('WebCrypto API not available');
}

function toBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(value) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function derivePasswordHash(password, saltBytes, iterations = KDF_ITERATIONS) {
  const webCrypto = getCrypto();
  const encoder = new TextEncoder();
  try {
    const keyMaterial = await webCrypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits']
    );
    const bits = await webCrypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: saltBytes,
        iterations,
        hash: 'SHA-256'
      },
      keyMaterial,
      DERIVED_BITS
    );
    return toBase64(new Uint8Array(bits));
  } catch {
    // Fallback per ambienti WebCrypto parziali: hash iterativo deterministico.
    let current = encoder.encode(`${toBase64(saltBytes)}:${password}`);
    const fallbackIterations = Math.max(1000, Math.floor(iterations / 100));
    for (let i = 0; i < fallbackIterations; i++) {
      const digest = await webCrypto.subtle.digest('SHA-256', current);
      current = new Uint8Array(digest);
    }
    return toBase64(current);
  }
}

function parseStoredHash(hashValue) {
  if (!hashValue || typeof hashValue !== 'string') return null;
  const parts = hashValue.split('$');
  if (parts.length !== 4 || parts[0] !== HASH_VERSION) return null;
  const iterations = Number(parts[1]);
  if (!Number.isFinite(iterations) || iterations < 1) return null;
  return {
    iterations,
    salt: parts[2],
    hash: parts[3]
  };
}

async function hashPasswordLegacy(password) {
  const webCrypto = getCrypto();
  const encoder = new TextEncoder();
  const data = encoder.encode(password + LEGACY_PASSWORD_SALT);
  const hashBuffer = await webCrypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Verificatore password da `GET OfflineSync/users` (admin).
 * @param {unknown} row
 * @returns {string | null}
 */
export function extractOfflinePasswordVerifier(row) {
  if (!row || typeof row !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */row;
  const raw = pickDirectoryField(
    record,
    'offlinePasswordVerifier',
    'OfflinePasswordVerifier',
    'offline_password_verifier'
  );
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Verificatore legacy Argon2 — non supportato per login offline PBKDF2 v2.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isArgon2OfflineVerifier(value) {
  return typeof value === 'string' && value.trim().startsWith('$argon2');
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOfflinePasswordVerifierFormat(value) {
  if (typeof value !== 'string' || !value) return false;
  const parts = value.split('$');
  if (parts.length !== 4 || parts[0] !== HASH_VERSION) return false;
  const iterations = Number(parts[1]);
  return Number.isFinite(iterations) && iterations >= 1 && Boolean(parts[2]) && Boolean(parts[3]);
}

/**
 * @param {unknown} row
 * @returns {string | null}
 */
function extractDirectoryUsername(row) {
  if (!row || typeof row !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */row;
  const raw = pickDirectoryField(record, 'username', 'Username', 'user_name');
  if (raw == null) return null;
  const username = String(raw).trim();
  return username.length > 0 ? username : null;
}

/**
 * @param {unknown} row
 * @param {string} username
 * @returns {string}
 */
function buildDirectoryDisplayName(row, username) {
  if (!row || typeof row !== 'object') return username;
  const record = /** @type {Record<string, unknown>} */row;
  const name = pickDirectoryField(record, 'name', 'Name', 'first_name');
  const surname = pickDirectoryField(record, 'surname', 'Surname', 'last_name');
  const full = [name, surname].
  filter((part) => part != null && String(part).trim() !== '').
  map((part) => String(part).trim()).
  join(' ').
  trim();
  return full || username;
}

/**
 * @typedef {Object} OfflineLoginImportStats
 * @property {number} imported
 * @property {number} skippedNoVerifier
 * @property {number} skippedArgon2
 * @property {number} skippedInvalidFormat
 */

/**
 * Popola/aggiorna `offline_login_users` dai verificatori della rubrica admin (`OfflineSync/users`).
 * @param {unknown[]} directoryUsers
 * @returns {Promise<OfflineLoginImportStats>}
 */
export async function upsertOfflineLoginUsersFromDirectory(directoryUsers) {
  const empty = {
    imported: 0,
    skippedNoVerifier: 0,
    skippedArgon2: 0,
    skippedInvalidFormat: 0
  };
  if (!Array.isArray(directoryUsers) || directoryUsers.length === 0) return empty;

  try {
    const existing = await getOfflineUsers();
    const byKey = new Map(
      existing.map((u) => [u.username.trim().toLowerCase(), u])
    );

    const stats = { ...empty };
    for (const row of directoryUsers) {
      const username = extractDirectoryUsername(row);
      if (!username) continue;

      const verifier = extractOfflinePasswordVerifier(row);
      if (!verifier) {
        stats.skippedNoVerifier += 1;
        continue;
      }
      if (isArgon2OfflineVerifier(verifier)) {
        stats.skippedArgon2 += 1;
        continue;
      }
      if (!isOfflinePasswordVerifierFormat(verifier)) {
        stats.skippedInvalidFormat += 1;
        continue;
      }

      const key = username.toLowerCase();
      const prev = byKey.get(key);
      byKey.set(key, {
        username,
        displayName: buildDirectoryDisplayName(row, username),
        passwordHash: verifier,
        lastLogin: prev?.lastLogin ?? 0
      });
      stats.imported += 1;
    }

    if (stats.imported === 0) return stats;

    await offlineDb.offlineData.put({
      key: OFFLINE_LOGIN_USERS_KEY,
      data: { users: Array.from(byKey.values()) },
      updatedAt: new Date().toISOString()
    });
    return stats;
  } catch (err) {

    return empty;
  }
}

/**
 * Recupera la lista di utenti disponibili per login offline.
 * @returns {Promise<Array<{ username: string, displayName: string, passwordHash: string, lastLogin: number }>>}
 */
export async function getOfflineUsers() {
  try {
    const record = await offlineDb.offlineData.get(OFFLINE_LOGIN_USERS_KEY);
    const data = record?.data;
    if (data && typeof data === 'object' && Array.isArray(data.users)) {
      return data.users;
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Rimuove le credenziali salvate per login offline (es. dopo reset password da admin).
 * @param {string} username
 */
export async function removeOfflineUserByUsername(username) {
  if (typeof username !== 'string' || !username.trim()) return;
  const want = username.trim().toLowerCase();
  try {
    const existing = await getOfflineUsers();
    const filtered = existing.filter((u) => u.username.trim().toLowerCase() !== want);
    if (filtered.length === existing.length) return;
    await offlineDb.offlineData.put({
      key: OFFLINE_LOGIN_USERS_KEY,
      data: { users: filtered },
      updatedAt: new Date().toISOString()
    });
  } catch (err) {

  }
}

/**
 * Salva un utente per il login offline (dopo login online riuscito sul dispositivo).
 * @param {Object} user
 * @param {string} user.username
 * @param {string} [user.displayName]
 * @param {string} user.passwordHash
 */
export async function saveOfflineUser(user) {
  try {
    const existing = await getOfflineUsers();
    const key = user.username.trim().toLowerCase();
    const filtered = existing.filter((u) => u.username.trim().toLowerCase() !== key);
    const updated = [
    {
      username: user.username,
      displayName: user.displayName || user.username,
      passwordHash: user.passwordHash,
      lastLogin: Date.now()
    },
    ...filtered];


    await offlineDb.offlineData.put({
      key: OFFLINE_LOGIN_USERS_KEY,
      data: { users: updated },
      updatedAt: new Date().toISOString()
    });
  } catch (err) {

  }
}

/**
 * @param {string} password
 * @returns {Promise<string>}
 */
export async function hashPasswordForOffline(password) {
  const webCrypto = getCrypto();
  const salt = new Uint8Array(SALT_BYTES);
  webCrypto.getRandomValues(salt);
  const hash = await derivePasswordHash(password, salt, KDF_ITERATIONS);
  return `${HASH_VERSION}$${KDF_ITERATIONS}$${toBase64(salt)}$${hash}`;
}

/**
 * @param {string} username
 * @param {string} password
 * @returns {Promise<{ valid: boolean, user?: { username: string, displayName: string, passwordHash: string, lastLogin: number } }>}
 */
export async function verifyOfflineCredentials(username, password) {
  try {
    const users = await getOfflineUsers();
    const want = username.trim().toLowerCase();
    const found = users.find((u) => u.username.trim().toLowerCase() === want);
    if (!found) {
      return { valid: false };
    }
    const parsed = parseStoredHash(found.passwordHash);
    if (parsed) {
      const derived = await derivePasswordHash(
        password,
        fromBase64(parsed.salt),
        parsed.iterations
      );
      if (derived === parsed.hash) {
        return { valid: true, user: found };
      }
      return { valid: false };
    }

    const legacyHash = await hashPasswordLegacy(password);
    if (legacyHash === found.passwordHash) {
      const upgradedHash = await hashPasswordForOffline(password);
      await saveOfflineUser({
        ...found,
        passwordHash: upgradedHash
      });
      return { valid: true, user: found };
    }
    return { valid: false };
  } catch {
    return { valid: false };
  }
}